import React, { useState, useEffect, FC } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Row, Col, Button, Alert } from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import Swal from "sweetalert2";
import { Icon } from "../../../components/Component";
import usersApis from "../../../api/master/users";
import FormDatePicker from "../../form-components/DatePicker";
import sequenceApis from "../../../api/master/sequence";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

interface SelectOptions {
  label: string;
  value: string;
}

const SequenceMaster: FC<{
  id?: string | null;
  formAction?: string | null;
  activeModules?: any;
  setAction?: () => void;
  toggleModelPopUp: () => void;
}> = ({ id, formAction, toggleModelPopUp, activeModules }) => {
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [modules, setModules] = useState<SelectOptions[]>([]);
  const [module, setModule] = useState<any>({});
  const [action, setAction] = useState(formAction);
  const methods = useForm({
    mode: "onChange",
  });
  const [sitesOptions, setSitesOptions] = useState<SelectOptions[]>([]);
  const [filteredSites, setFilteredSites] = useState<SelectOptions[]>([]);
  const [originalData, setOriginalData] = useState<any[]>([]);

  const {
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = methods;
  const [alertInfo, setAlertInfo] = useState({
    type: "", // 'success' or 'error'
    message: "",
    errors: [],
  });
  const level = watch("level");
  const moduleId = watch("moduleId");
  const currentSiteId = watch("siteId");
  const sequenceType = watch("sequenceType");

  const fetchAllSequences = async () => {
    try {
      const response = await sequenceApis.fetchSequence();
      if (response && response.data) {
        setOriginalData(response.data);
      }
    } catch (error) {
      console.error("Error fetching sequences:", error);
    }
  };

  const onSubmit = async (data: any) => {
    delete data?.includeState;
    if (data.level !== "Site") {
      data.siteId = null;
    }
    if (!data.siteId || data.siteId === "") {
      data.siteId = null;
    }
    if (data.level === "Site") {
      const existing = originalData.find(
        (item: any) =>
          item.status === "Active" &&
          item.moduleId === (action === "edit" ? module?.id : data.moduleId) &&
          item.level === data.level &&
          item.siteId === data.siteId &&
          (action === "edit" ? item.id !== id : true)
      );

      if (existing) {
        setAlertInfo({
          type: "error",
          message:
            "An active sequence already exists for this module and site combination",
          errors: [],
        });
        return;
      }
    }
    if (!action && !id) {
      const response = await sequenceApis.createSequence(data);
      const { status, message } = response || { message: [] };
      if (status) {
        Swal.fire("Created!", "Sequence has been created", "success");
        toggleModelPopUp();
      } else {
        const alertMessage: string = Array.isArray(message)
          ? message.join(", ")
          : typeof message === "object"
          ? JSON.stringify(message)
          : message;
        setAlertInfo({
          type: "error",
          message: alertMessage,
          errors: [],
        });
      }
    } else if (action === "edit") {
      const response = await sequenceApis.updateSequenceById(id, {
        ...data,
        moduleId: module?.id,
      });
      const { status, message } = response || { message: [] };
      if (status) {
        Swal.fire("Updated!", "Sequence has been updated", "success");
        toggleModelPopUp();
      } else {
        const alertMessage: any = Array.isArray(message)
          ? message.join(", ")
          : typeof message === "object"
          ? JSON.stringify(message)
          : message;
        setAlertInfo({
          type: "error",
          message: alertMessage,
          errors: [],
        });
      }
    }
  };

  const filterSitesByModuleAndLevel = (moduleId: string, level: string) => {
    if (!moduleId || !level || !originalData.length) {
      return sitesOptions;
    }
    const existingSequences = originalData.filter(
      (item: any) =>
        item.status === "Active" &&
        item.moduleId.toString() === moduleId.toString() &&
        item.level === level
    );
    if (level === "Site") {
      const usedSiteIds = existingSequences.map((item: any) =>
        item.siteId?.toString()
      );

      return sitesOptions.filter((site: SelectOptions) => {
        const isUsed = usedSiteIds.includes(site.value);
        const isCurrentSite =
          action === "edit" && site.value === currentSiteId?.toString();

        return !isUsed || isCurrentSite;
      });
    }

    return sitesOptions;
  };

  useEffect(() => {
    if (sitesOptions.length === 0) return;

    if (level === "Site") {
      if (moduleId) {
        const filtered = filterSitesByModuleAndLevel(moduleId, level);
        setFilteredSites(filtered);

        if (
          currentSiteId &&
          !filtered.find((site) => site.value === currentSiteId)
        ) {
          if (action !== "edit") {
            setValue("siteId", null);
          }
        }
      } else {
        setFilteredSites(sitesOptions);
      }
    } else if (action === "edit" && currentSiteId) {
      // In edit mode, if there's a siteId, include it in filteredSites even if level is not "Site"
      const currentSite = sitesOptions.find(
        (site) => site.value === currentSiteId?.toString()
      );
      setFilteredSites(currentSite ? [currentSite] : []);
    } else {
      setFilteredSites([]);
      if (action !== "edit") {
        setValue("siteId", null);
      }
    }
  }, [
    moduleId,
    level,
    sitesOptions,
    action,
    id,
    currentSiteId,
    originalData,
    setValue,
  ]);

  const fetchModuleConfig = async (isMaster?: boolean) => {
    try {
      const res = await usersApis.fetchSequenceModules(isMaster);
      const moduleConfigData = res.data;
      const filteredModuleConfigData = [...moduleConfigData].filter(
        (module: any) => {
          return !activeModules?.includes(Number(module?.moduleID));
        }
      );
      setModules(
        filteredModuleConfigData.map((obj: any) => ({
          label: obj.moduleName,
          value: obj.moduleID.toString(),
        }))
      );
    } catch (err) {
      throw err;
    }
  };

  const fetchModuleSequenceById = async (id: any) => {
    try {
      const res = await sequenceApis.fetchSequenceById(id);
      const { data, status, message } = res;
      if (status) {
        setModule({ name: data.moduleName, id: data.moduleId });
        setValue("moduleId", data.moduleId);
        setValue("sequenceNo", data.sequenceNo);
        setValue("endDate", dayjs(data.endDate));
        setValue("prefix", data.prefix);
        setValue("level", data.level);
        setValue("sequenceType", data.sequenceType || "Transaction");
        setValue(
          "siteId",
          data.level === "Site" && data.siteId ? data.siteId : null
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (level && level !== "Site") {
      setValue("siteId", null);
    }
  }, [level, setValue]);

  useEffect(() => {
    if (sequenceType === "master") {
      setValue("level", "Org");
      setValue("siteId", null);
    }
  }, [sequenceType, setValue]);

  useEffect(() => {
    fetchAllSequences();
  }, []);

  useEffect(() => {
    if (sequenceType) {
      const isMaster = sequenceType === "master";
      fetchModuleConfig(isMaster);
      // Reset module selection when sequence type changes (only for new entries, not edit mode)
      if (!id) {
        setValue("moduleId", null);
      }
    } else {
      // Clear modules when sequence type is not selected
      setModules([]);
    }
  }, [sequenceType, id, setValue]);

  useEffect(() => {
    if (id) {
      fetchModuleSequenceById(id);
    }
  }, [id]);

  useEffect(() => {
    if (currentUser) {
      let sitesOptions = currentUser?.siteAccessibility?.map((item: any) => ({
        label: item.siteName,
        value: item.siteID.toString(),
      }));
      setSitesOptions(sitesOptions);
    }
  }, [currentUser]);

  return (
    <>
      <h5 className="title">
        {action === "edit"
          ? `Edit ${module?.name} Sequence`
          : action === "view"
          ? "View Sequence"
          : "Create Sequence"}
      </h5>
      {alertInfo.message && (
        <div className="mb-3 mt-1">
          <Alert
            color={alertInfo.type === "error" ? "danger" : "success"}
            className="alert-icon"
          >
            <strong>{alertInfo.message}</strong>
            <Icon
              name={
                alertInfo.type === "error" ? "alert-circle" : "check-circle"
              }
            />
            {alertInfo.errors.length > 0 ? (
              <ul>
                {alertInfo.errors.map((err: any, index: any) => (
                  <li key={index}>{err}</li>
                ))}
              </ul>
            ) : (
              ""
            )}
          </Alert>
        </div>
      )}
      <div style={{ marginTop: "15px" }}></div>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="row" style={{ marginBottom: "20px" }}>
            {action == "edit" && <div style={{ marginTop: "15px" }}></div>}

            <div className="col-md-4">
              <FormSelect
                name="sequenceType"
                label="Sequence Type"
                disabled={action === "view"}
                options={[
                  { label: "Masters", value: "master" },
                  { label: "Others", value: "Transaction" },
                ]}
                required={true}
              />
            </div>
            <div className="col-md-4">
              <FormSelect
                name="level"
                label=" Set Sequence For"
                disabled={action === "view" || sequenceType === "master"}
                options={
                  sequenceType === "master"
                    ? [{ label: "Organisation", value: "Org" }]
                    : [
                        { label: "Organisation", value: "Org" },
                        { label: "Site", value: "Site" },
                      ]
                }
                required={true}
              />
            </div>
            <div className="col-md-4">
              {action === "edit" ? (
                <FormSelect
                  name="moduleId"
                  label="Module"
                  options={
                    module?.name
                      ? [{ label: module.name, value: module.id.toString() }]
                      : []
                  }
                  disabled={true}
                  required={true}
                />
              ) : (
                <FormSelect
                  name="moduleId"
                  label="Module"
                  options={modules}
                  required={true}
                />
              )}
            </div>
          </div>

          <div className="row" style={{ marginBottom: "20px" }}>
            <div className="col-md-4">
              <FormInput
                name="prefix"
                label="PREFIX"
                type="text"
                maxLength={20}
                placeholder="Enter Prefix"
                required={true}
              />
            </div>
            <div className="col-md-4">
              <FormInput
                name="sequenceNo"
                label="Sequence No"
                disabled={action === "view"}
                type="Number"
                placeholder="Enter Sequence Number"
                required={true}
              />
            </div>
            {action !== "edit" && (
              <div className="col-md-4">
                <FormDatePicker
                  disabled={action === "view"}
                  name="endDate"
                  label="End Date"
                  minDate={new Date()}
                  required={true}
                />
              </div>
            )}
          </div>

          {(level === "Site" || (action === "edit" && currentSiteId)) && (
            <div className="row">
              <div className="col-md-4">
                <FormSelect
                  name="siteId"
                  label="Site"
                  placeholder={
                    filteredSites.length === 0 && moduleId
                      ? "No available sites for this module"
                      : "Select Site"
                  }
                  options={filteredSites}
                  disabled={
                    action === "edit" ||
                    (filteredSites.length === 0 && !!moduleId)
                  }
                  required={level === "Site"}
                />
              </div>
            </div>
          )}

          {action !== "view" ? (
            <Row className="mt-4">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button
                      className="btn btn-primary btn-md"
                      type="submit"
                      color="primary"
                    >
                      <span>{action === "edit" ? "Update" : "Submit"}</span>
                    </Button>
                  </li>
                  <li>
                    <a
                      href="#cancel"
                      onClick={(ev) => {
                        ev.preventDefault();
                        toggleModelPopUp();
                      }}
                      className="link link-light"
                    >
                      Cancel
                    </a>
                  </li>
                </ul>
              </Col>
            </Row>
          ) : (
            <Row className="mt-4">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <div
                      className="btn btn-primary btn-md"
                      color="primary"
                      onClick={() => setAction("edit")}
                    >
                      <Icon name="pen" />
                      <span>Edit</span>
                    </div>
                  </li>
                </ul>
              </Col>
            </Row>
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default SequenceMaster;
